import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fileToBase64, bankFieldsFor, CREDIT_CARD_FIELDS } from "@/lib/util";
import CurrencyIcon from "@/components/CurrencyIcon";
import { toast } from "sonner";
import { UploadSimple, WarningCircle, X, Bank, Wallet, CheckCircle } from "@phosphor-icons/react";

// Client-side wallet validation (mirrors backend/wallet_validate.py)
const TRON_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
const EVM_RE = /^0x[0-9a-fA-F]{40}$/;
const BTC_LEGACY_RE = /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/;
const BTC_BECH32_RE = /^(bc1)[0-9a-z]{25,89}$/i;
const SOL_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

function familyOf(network) {
  const n = String(network || "").toUpperCase().trim();
  if (["TRC20", "TRC-20", "TRON"].includes(n)) return "tron";
  if (["ERC20", "ERC-20", "ETH", "ETHEREUM", "BEP20", "BEP-20", "BSC", "POLYGON", "MATIC", "ARBITRUM", "OPTIMISM", "BASE"].includes(n)) return "evm";
  if (["BTC", "BITCOIN"].includes(n)) return "btc";
  if (["SOL", "SOLANA", "SPL"].includes(n)) return "sol";
  return null;
}

function validateWalletClient(address, network) {
  if (!address) return { ok: null, message: "" };
  const fam = familyOf(network);
  const a = address.trim();
  if (fam === "tron") return TRON_RE.test(a) ? { ok: true, message: "Valid Tron address" } : { ok: false, message: "Not a valid Tron address (T…, 34 chars)" };
  if (fam === "evm") return EVM_RE.test(a) ? { ok: true, message: "Valid EVM address" } : { ok: false, message: "Not a valid EVM address (0x…, 42 chars)" };
  if (fam === "btc") return (BTC_BECH32_RE.test(a) || BTC_LEGACY_RE.test(a)) ? { ok: true, message: "Valid Bitcoin address" } : { ok: false, message: "Not a valid Bitcoin address" };
  if (fam === "sol") return SOL_RE.test(a) ? { ok: true, message: "Valid Solana address" } : { ok: false, message: "Not a valid Solana address (base58, 32–44 chars)" };
  return { ok: null, message: "Select a network to validate" };
}

// Fields to capture per currency family



export default function NewRequest() {
  const nav = useNavigate();
  const [brands, setBrands] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [payers, setPayers] = useState([]);
  const [form, setForm] = useState({
    brand_id: "",
    vendor_name: "",
    expense_type: "",
    billing_month: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`,
    billing_month_end: "",
    currency_code: "",
    amount: "",
    payment_mode: "bank",
    network: "",
    wallet_address: "",
    bank_details: {},
    remarks: "",
    needs_approval: true,
    assigned_payer_email: "",
  });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [addingVendor, setAddingVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");
  const [addingType, setAddingType] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  // Multi-brand split: array of {brand_id, amount}. When empty, this is a
  // single-brand request; when populated, allocations replace the primary
  // brand amount and must sum to `form.amount`.
  const [splitOpen, setSplitOpen] = useState(false);
  const [allocations, setAllocations] = useState([]);  // [{brand_id, amount}]

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data.filter((b) => b.is_active))).catch(() => {});
    api.get("/currencies").then((r) => setCurrencies(r.data.filter((c) => c.is_active))).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data.filter((v) => v.is_active !== false))).catch(() => setVendors([]));
    api.get("/expense-types").then((r) => setExpenseTypes(r.data.filter((t) => t.is_active !== false))).catch(() => setExpenseTypes([]));
    api.get("/users/payers-list").then((r) => setPayers(r.data || [])).catch(() => setPayers([]));
  }, []);

  useEffect(() => {
    const { brand_id, vendor_name, billing_month, amount } = form;
    if (!brand_id || !vendor_name || !billing_month || !amount) { setDupes([]); return; }
    const t = setTimeout(() => {
      api.get("/payments/duplicate-check", { params: { brand_id, vendor_name, billing_month, amount } })
        .then((r) => setDupes(r.data.duplicates || []))
        .catch(() => setDupes([]));
    }, 400);
    return () => clearTimeout(t);
  }, [form.brand_id, form.vendor_name, form.billing_month, form.amount]);

  // USDT (and any other crypto) skips auditor approval by default
  useEffect(() => {
    if (!form.currency_code) return;
    const c = currencies.find((x) => x.code === form.currency_code);
    if (!c) return;
    setForm((s) => ({ ...s, needs_approval: c.code === "USDT" ? false : true }));
  }, [form.currency_code, currencies]);

  const currency = currencies.find((c) => c.code === form.currency_code);
  const isCrypto = currency?.is_crypto;
  const bankFields = isCrypto ? [] : bankFieldsFor(form.currency_code);

  const setF = (k) => (v) => setForm((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const setBank = (k, upper = false) => (e) => {
    const val = upper ? String(e.target.value).toUpperCase() : e.target.value;
    setForm((s) => ({ ...s, bank_details: { ...s.bank_details, [k]: val } }));
  };

  const addVendor = async () => {
    if (!newVendorName.trim()) return;
    try {
      const { data } = await api.post("/vendors", { name: newVendorName.trim() });
      setVendors((prev) => [...prev, data]);
      setForm((s) => ({ ...s, vendor_name: data.name }));
      setAddingVendor(false);
      setNewVendorName("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const addType = async () => {
    if (!newTypeName.trim()) return;
    try {
      const { data } = await api.post("/expense-types", { name: newTypeName.trim() });
      setExpenseTypes((prev) => [...prev, data]);
      setForm((s) => ({ ...s, expense_type: data.name }));
      setAddingType(false);
      setNewTypeName("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand_id || !form.currency_code || !form.amount || !form.vendor_name) {
      toast.error("Please fill required fields");
      return;
    }
    // Bank/credit-card details are NOT required at creation — requester may add
    // them later via the Edit dialog. Format-check whatever IS provided.
    if (isCrypto && form.wallet_address) {
      const v = validateWalletClient(form.wallet_address, form.network);
      if (v.ok === false) { toast.error(v.message); return; }
    }
    if (!form.assigned_payer_email) {
      toast.error("Please assign a payer");
      return;
    }
    // Validate multi-brand allocation totals (client-side hint; server enforces)
    let cleanedAllocs = null;
    if (splitOpen && allocations.length > 0) {
      const total = allocations.reduce((s, a) => s + Number(a.amount || 0), 0);
      if (Math.abs(total - Number(form.amount)) > 0.01) {
        toast.error(`Brand allocations sum to ${total.toFixed(2)} — must equal ${Number(form.amount).toFixed(2)}`);
        return;
      }
      const bad = allocations.find((a) => !a.brand_id || !Number(a.amount));
      if (bad) { toast.error("Every allocation row needs a brand and an amount"); return; }
      cleanedAllocs = allocations.map((a) => ({
        brand_id: a.brand_id, amount: Number(a.amount), note: a.note || null,
      }));
    }
    setSaving(true);
    try {
      const attachments = [];
      for (const f of files) attachments.push(await fileToBase64(f));
      const payload = {
        brand_id: form.brand_id,
        vendor_name: form.vendor_name,
        expense_type: form.expense_type || null,
        billing_month: form.billing_month,
        currency_code: form.currency_code,
        amount: Number(form.amount),
        payment_mode: isCrypto ? "crypto" : form.payment_mode,
        network: isCrypto ? form.network : null,
        wallet_address: isCrypto ? form.wallet_address : null,
        bank_details: isCrypto || (form.payment_mode !== "bank" && form.payment_mode !== "credit_card") ? {} : form.bank_details,
        remarks: form.remarks,
        needs_approval: form.needs_approval,
        assigned_payer_email: form.assigned_payer_email ? form.assigned_payer_email.trim() : null,
        attachments,
        brand_allocations: cleanedAllocs,
      };
      const { data } = await api.post("/payments", payload);
      toast.success(`Request ${data.reference} created`);
      nav(`/requests/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-4xl">
      <header className="mb-6">
        <div className="overline">Create</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">
          <span className="text-gold-gradient">New payment request</span>
        </h1>
        <p className="text-sm text-slate-600 mt-1">Submit for audit and payment.</p>
      </header>

      {dupes.length > 0 && (
        <div data-testid="duplicate-warning" className="mb-6 rounded-2xl border border-yellow-300 bg-yellow-50/80 p-4 flex items-start gap-3">
          <WarningCircle size={20} className="text-yellow-700 flex-shrink-0 mt-0.5" weight="fill" />
          <div className="text-sm">
            <div className="font-semibold text-yellow-900">Possible duplicate</div>
            <div className="text-yellow-800">Found {dupes.length} existing request(s) with same brand, vendor, month and amount:
              <ul className="mt-2 list-disc list-inside font-mono text-xs">
                {dupes.map((d) => <li key={d.id}>{d.reference} — {d.stage}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="glass rounded-3xl p-5 sm:p-8 space-y-6 shimmer-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Brand *">
            <Select value={form.brand_id} onValueChange={setF("brand_id")}>
              <SelectTrigger data-testid="new-brand"><SelectValue placeholder="Select brand" /></SelectTrigger>
              <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="mt-2 flex items-center gap-3">
              <label className="text-xs flex items-center gap-1.5 text-slate-600 cursor-pointer">
                <input
                  data-testid="new-split-toggle"
                  type="checkbox"
                  checked={splitOpen}
                  onChange={(e) => {
                    setSplitOpen(e.target.checked);
                    if (e.target.checked && allocations.length === 0 && form.brand_id) {
                      // Seed with the primary brand and full amount
                      setAllocations([{ brand_id: form.brand_id, amount: form.amount, note: "" }]);
                    }
                  }}
                  className="accent-amber-600"
                />
                Split this payment across multiple brands
              </label>
            </div>
            {splitOpen && (
              <div data-testid="new-split-panel" className="mt-3 rounded-xl border border-amber-200 bg-amber-50/40 p-3 space-y-2">
                {allocations.map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Select value={a.brand_id} onValueChange={(v) => {
                      const next = [...allocations]; next[i] = { ...next[i], brand_id: v }; setAllocations(next);
                    }}>
                      <SelectTrigger data-testid={`new-split-brand-${i}`} className="flex-1 h-9"><SelectValue placeholder="Brand" /></SelectTrigger>
                      <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input
                      data-testid={`new-split-amount-${i}`}
                      type="number" step="0.01" min="0"
                      value={a.amount}
                      onChange={(e) => {
                        const next = [...allocations]; next[i] = { ...next[i], amount: e.target.value }; setAllocations(next);
                      }}
                      placeholder="Amount"
                      className="w-32 h-9"
                    />
                    <button type="button" onClick={() => setAllocations(allocations.filter((_, j) => j !== i))}
                            className="text-red-600 hover:text-red-800 text-xs font-bold px-2"
                            data-testid={`new-split-remove-${i}`}>×</button>
                  </div>
                ))}
                <button
                  type="button"
                  data-testid="new-split-add"
                  onClick={() => setAllocations([...allocations, { brand_id: "", amount: "", note: "" }])}
                  className="text-xs font-semibold text-amber-800 hover:text-amber-950"
                >+ Add brand allocation</button>
                <div className="text-[11px] text-slate-600 font-mono">
                  Allocated: {allocations.reduce((s, a) => s + Number(a.amount || 0), 0).toFixed(2)}
                  {" · Request amount: "}{Number(form.amount || 0).toFixed(2)}
                  {allocations.length > 0 && Math.abs(allocations.reduce((s, a) => s + Number(a.amount || 0), 0) - Number(form.amount || 0)) > 0.01 &&
                    <span className="ml-2 text-red-700">✗ mismatch</span>}
                  {allocations.length > 0 && Math.abs(allocations.reduce((s, a) => s + Number(a.amount || 0), 0) - Number(form.amount || 0)) < 0.01 &&
                    <span className="ml-2 text-emerald-700">✓ matches</span>}
                </div>
              </div>
            )}
          </Field>

          <Field label="Vendor / Company *">
            {!addingVendor ? (
              <div className="flex gap-2">
                <Select value={form.vendor_name} onValueChange={setF("vendor_name")}>
                  <SelectTrigger data-testid="new-vendor" className="flex-1"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}
                    {vendors.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No vendors yet — add one</div>}
                  </SelectContent>
                </Select>
                <Button type="button" data-testid="new-add-vendor-btn" onClick={() => setAddingVendor(true)} className="amber-pill rounded-xl h-10 px-3">+ Add</Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input data-testid="new-vendor-name" value={newVendorName} onChange={(e) => setNewVendorName(e.target.value)} placeholder="New vendor name" />
                <Button type="button" data-testid="new-vendor-save" onClick={addVendor} className="amber-pill rounded-xl h-10 px-3">Save</Button>
                <Button type="button" variant="outline" onClick={() => { setAddingVendor(false); setNewVendorName(""); }} className="rounded-xl h-10">Cancel</Button>
              </div>
            )}
          </Field>

          <Field label="Expense type">
            {!addingType ? (
              <div className="flex gap-2">
                <Select value={form.expense_type} onValueChange={setF("expense_type")}>
                  <SelectTrigger data-testid="new-expense-type" className="flex-1"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {expenseTypes.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                    {expenseTypes.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No types yet</div>}
                  </SelectContent>
                </Select>
                <Button type="button" data-testid="new-add-type-btn" onClick={() => setAddingType(true)} className="amber-pill rounded-xl h-10 px-3">+ Add</Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input data-testid="new-type-name" value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} placeholder="New expense type" />
                <Button type="button" data-testid="new-type-save" onClick={addType} className="amber-pill rounded-xl h-10 px-3">Save</Button>
                <Button type="button" variant="outline" onClick={() => { setAddingType(false); setNewTypeName(""); }} className="rounded-xl h-10">Cancel</Button>
              </div>
            )}
          </Field>

          <Field label="Billing month *">
            <Input data-testid="new-billing-month" type="month" value={form.billing_month} onChange={setF("billing_month")} required />
          </Field>

          <Field label="Currency *">
            <Select value={form.currency_code} onValueChange={setF("currency_code")}>
              <SelectTrigger data-testid="new-currency"><SelectValue placeholder="Select currency" /></SelectTrigger>
              <SelectContent>{currencies.map((c) => (
                <SelectItem key={c.id} value={c.code}>
                  <span className="inline-flex items-center gap-2"><CurrencyIcon code={c.code} size={16} /> {c.code} — {c.name}</span>
                </SelectItem>
              ))}</SelectContent>
            </Select>
          </Field>

          <Field label="Amount vendor should receive *">
            <Input data-testid="new-amount" type="number" step="any" value={form.amount} onChange={setF("amount")} required />
          </Field>

          {!isCrypto && (
            <Field label="Payment mode">
              <Select value={form.payment_mode} onValueChange={setF("payment_mode")}>
                <SelectTrigger data-testid="new-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank">Bank transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="credit_card">Credit card</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}

          {isCrypto && (
            <>
              <Field label="Network *">
                <Select value={form.network} onValueChange={setF("network")}>
                  <SelectTrigger data-testid="new-network"><SelectValue placeholder="Select network" /></SelectTrigger>
                  <SelectContent>
                    {(currency?.networks || []).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Wallet address (optional — add later)">
                <WalletField
                  value={form.wallet_address}
                  onChange={setF("wallet_address")}
                  network={form.network}
                />
              </Field>
            </>
          )}
        </div>

        {/* Bank details section */}
        {!isCrypto && form.payment_mode === "bank" && form.currency_code && (
          <div className="rounded-2xl border border-amber-200/60 bg-white/60 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Bank size={18} weight="duotone" className="text-amber-700" />
              <div className="font-heading font-bold text-sm tracking-tight">Recipient bank details — {form.currency_code}</div>
            </div>
            <div className="text-[10px] text-slate-500 mb-3">All fields are optional at creation — you can add them later from the invoice&apos;s Edit dialog.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {bankFields.map((bf) => (
                <Field key={bf.k} label={bf.label}>
                  <Input
                    data-testid={`new-bank-${bf.k}`}
                    value={form.bank_details?.[bf.k] || ""}
                    onChange={setBank(bf.k, bf.upper)}
                    className={bf.mono ? "font-mono text-xs" : ""}
                  />
                </Field>
              ))}
            </div>
          </div>
        )}

        {/* Credit card details section */}
        {!isCrypto && form.payment_mode === "credit_card" && (
          <div className="rounded-2xl border border-amber-200/60 bg-white/60 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Bank size={18} weight="duotone" className="text-amber-700" />
              <div className="font-heading font-bold text-sm tracking-tight">Credit card details</div>
            </div>
            <div className="text-[10px] text-slate-500 mb-3">All fields are optional at creation — you can add them later from the invoice&apos;s Edit dialog.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CREDIT_CARD_FIELDS.map((bf) => (
                <Field key={bf.k} label={bf.label}>
                  <Input
                    data-testid={`new-cc-${bf.k}`}
                    value={form.bank_details?.[bf.k] || ""}
                    onChange={setBank(bf.k)}
                    className={bf.mono ? "font-mono text-xs" : ""}
                    placeholder={bf.k === "last_4" ? "1234" : bf.k === "expiry" ? "MM/YY" : ""}
                    maxLength={bf.k === "last_4" ? 4 : undefined}
                  />
                </Field>
              ))}
            </div>
            <div className="text-[10px] text-slate-500 mt-3">Never enter the full card number — capture only the last 4 for reconciliation.</div>
          </div>
        )}

        {isCrypto && form.wallet_address && (
          <div className="rounded-2xl border border-amber-200/60 bg-white/60 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Wallet size={18} weight="duotone" className="text-amber-700" />
              <div className="font-heading font-bold text-sm">Payer will send to:</div>
            </div>
            <div className="font-mono text-xs break-all">{form.wallet_address} <span className="text-slate-500">({form.network || "network"})</span></div>
          </div>
        )}

        {currency && form.amount && Number(form.amount) > 0 && (
          <div data-testid="charge-preview" className="rounded-2xl p-4 flex items-center justify-between gap-3"
            style={{ background: "linear-gradient(135deg, rgba(134, 239, 172, 0.35), rgba(255, 253, 246, 0.9))", border: "1px solid rgba(21, 128, 61, 0.25)" }}>
            <div className="text-sm">
              <div className="overline mb-1">Payer will transfer</div>
              <div className="text-xs text-slate-600">
                Vendor receives <span className="font-mono font-semibold">{Number(form.amount).toLocaleString()} {currency.code}</span>
              </div>
            </div>
            <div className="font-heading font-black text-3xl text-green-700 font-mono">
              {Number(form.amount).toFixed(currency.decimals || 2)} {currency.code}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Assign payer *">
            <Select
              value={form.assigned_payer_email || ""}
              onValueChange={(v) => setForm((s) => ({ ...s, assigned_payer_email: v }))}
            >
              <SelectTrigger data-testid="new-assigned-payer" className="w-full">
                <SelectValue placeholder="Select a payer…" />
              </SelectTrigger>
              <SelectContent className="max-h-64 max-w-[var(--radix-select-trigger-width)] w-[var(--radix-select-trigger-width)]" position="popper" sideOffset={4}>
                {payers.map((p) => (
                  <SelectItem key={p.id} value={p.email} className="truncate">
                    <span className="truncate block max-w-full">{p.name} · {p.role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-slate-500 mt-1">The invoice appears on the assigned payer&apos;s dashboard.</div>
          </Field>
          <Field label="Remarks / notes">
            <Textarea data-testid="new-remarks" value={form.remarks} onChange={setF("remarks")} rows={3} placeholder="Any context for the auditor…" />
          </Field>
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-700 tracking-wide">Approval required</Label>
          <div className="mt-2 flex items-center gap-3 h-10">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="new-needs-approval"
                checked={form.needs_approval}
                onChange={(e) => setForm((s) => ({ ...s, needs_approval: e.target.checked }))}
              />
              Needs auditor approval before payment
            </label>
          </div>
        </div>

        <div>
          <Label>Attachments (invoice, quote, etc.)</Label>
          <label className="mt-2 flex items-center gap-2 rounded-xl border border-dashed border-amber-300 bg-white/50 p-4 cursor-pointer hover:bg-amber-50/50">
            <UploadSimple size={18} /> <span className="text-sm">Click to upload files</span>
            <input data-testid="new-files" type="file" multiple className="hidden" onChange={(e) => setFiles([...files, ...Array.from(e.target.files)])} />
          </label>
          {files.length > 0 && (
            <ul className="mt-2 space-y-1">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-xs font-mono">
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-slate-500">{Math.round(f.size / 1024)} KB</span>
                  <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-red-600"><X size={14} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-amber-100">
          <Button data-testid="new-submit" type="submit" disabled={saving} className="amber-pill rounded-xl h-11 px-6">
            {saving ? "Submitting…" : "Submit for audit"}
          </Button>
          <Button type="button" variant="outline" onClick={() => nav(-1)} className="rounded-xl h-11">Cancel</Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-700 tracking-wide">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function WalletField({ value, onChange, network }) {
  const v = validateWalletClient(value, network);
  return (
    <div>
      <div className="relative">
        <Input
          data-testid="new-wallet"
          value={value}
          onChange={onChange}
          placeholder={network ? `Recipient ${network} address` : "Select network first"}
          className={`font-mono text-xs pr-9 ${v.ok === true ? "border-emerald-400 focus-visible:ring-emerald-300" : v.ok === false ? "border-red-400 focus-visible:ring-red-300" : ""}`}
        />
        {value && v.ok === true && (
          <CheckCircle size={16} weight="fill" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-600" />
        )}
        {value && v.ok === false && (
          <WarningCircle size={16} weight="fill" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-red-600" />
        )}
      </div>
      {value && v.message && (
        <div data-testid="wallet-validation-msg" className={`mt-1 text-[11px] font-mono ${v.ok === true ? "text-emerald-700" : v.ok === false ? "text-red-700" : "text-slate-500"}`}>
          {v.message}
        </div>
      )}
    </div>
  );
}
